import React, { useRef, useState } from 'react';
import './MessageInput.css';

const MessageInput = ({ 
  onSend, 
  onFileUpload, 
  replyingTo, 
  onCancelReply,
  threadId,
  placeholder,
  currentRoom 
}) => {
  const [content, setContent] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    onSend(content, threadId);
    setContent('');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('roomId', currentRoom._id);

      const response = await fetch('/api/chat/rooms/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        onFileUpload(data.data);
      } else {
        alert('Failed to upload file');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const insertMarkdown = (syntax) => {
    const textarea = document.querySelector('.message-input-field');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newText;
    switch (syntax) {
      case 'bold':
        newText = content.substring(0, start) + `**${selectedText}**` + content.substring(end);
        break;
      case 'italic':
        newText = content.substring(0, start) + `*${selectedText}*` + content.substring(end);
        break;
      case 'strikethrough':
        newText = content.substring(0, start) + `~~${selectedText}~~` + content.substring(end);
        break;
      case 'code':
        newText = content.substring(0, start) + `\`${selectedText}\`` + content.substring(end);
        break;
      case 'codeblock':
        newText = content.substring(0, start) + `\n\`\`\`\n${selectedText}\n\`\`\`\n` + content.substring(end);
        break;
      default:
        return;
    }
    
    setContent(newText);
    textarea.focus();
  };

  return (
    <div className="message-input-container">
      {replyingTo && (
        <div className="reply-preview-bar">
          <div className="reply-content">
            <span className="reply-label">Replying to {replyingTo.senderId?.username}</span>
            <span className="reply-text">{replyingTo.content.substring(0, 50)}...</span>
          </div>
          <button className="cancel-reply-btn" onClick={onCancelReply}>×</button>
        </div>
      )}

      <div className="input-toolbar">
        <button 
          type="button" 
          className="toolbar-btn" 
          onClick={() => insertMarkdown('bold')}
          title="Bold (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button 
          type="button" 
          className="toolbar-btn" 
          onClick={() => insertMarkdown('italic')}
          title="Italic (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <button 
          type="button" 
          className="toolbar-btn" 
          onClick={() => insertMarkdown('strikethrough')}
          title="Strikethrough"
        >
          <s>S</s>
        </button>
        <button 
          type="button" 
          className="toolbar-btn" 
          onClick={() => insertMarkdown('code')}
          title="Inline code"
        >
          &lt;/&gt;
        </button>
        <button 
          type="button" 
          className="toolbar-btn" 
          onClick={() => insertMarkdown('codeblock')}
          title="Code block"
        >
          {'{ }'}
        </button>
        <div className="toolbar-divider" />
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,video/*,.pdf,.doc,.docx,.txt"
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingFile}
          title="Upload file (max 10MB)"
        >
          {uploadingFile ? '⏳' : '📎'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="input-form">
        <textarea
          className="message-input-field"
          placeholder={placeholder || 'Type a message... (Markdown supported)'}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          rows={2}
        />
        <button 
          type="submit" 
          className="send-btn" 
          disabled={!content.trim() || uploadingFile}
        >
          ➤
        </button>
      </form>

      <div className="input-hints">
        <small>
          <strong>Tips:</strong> Use **bold**, *italic*, `code`, ```code block```, ~~strikethrough~~
          | Press Enter to send, Shift+Enter for new line
        </small>
      </div>
    </div>
  );
};

export default MessageInput;
